import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '@/stores/auth';
import { getDeviceId } from '@/lib/deviceId';
import { sessionStorageAdapter } from '@/lib/secureStorage';
import { isStoreBuild } from '@/lib/platform';
import { reportNetworkError } from '@/lib/networkNotice';
import { isAuthScreen, loginPathWithNext } from '@/lib/loginReturn';
import { nativeSafeAdapter } from '@/lib/nativeHttp';
import { markCleanExit } from '@/lib/crashReport';

const BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api/v1';

/**
 * Откуда на самом деле берутся файлы платформы.
 *
 * ⚠️ ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Сервер отдаёт ссылки на файлы ОТНОСИТЕЛЬНЫМИ —
 * `/api/v1/files/o/<ключ>`. В браузере это работает: страница и файлы лежат на
 * одном адресе. А приложение из магазина открывает свою страницу с адреса
 * `capacitor://localhost`, и такая ссылка ведёт внутрь самого приложения, где
 * никаких файлов нет. Снаружи это выглядит именно так, как сказал заказчик
 * 22.08.2026: «в мобилке ничего не работает, как будто там какой-то локалхост» —
 * не открывались ни аудио заданий, ни картинки, ни обложки.
 *
 * Чинить в пятидесяти местах, где рисуются `<img>`, `<audio>` и `<video>`, —
 * значит гарантированно забыть половину и снова забыть в следующем экране.
 * Поэтому правим ОДИН раз, на входе: любую ссылку вида `/api/v1/...`,
 * пришедшую в ответе, разворачиваем в полную.
 */
const API_ORIGIN = (() => {
  try {
    return new URL(BASE_PATH, window.location.href).origin;
  } catch {
    return '';
  }
})();

/**
 * Разворачивает относительные ссылки в теле ответа.
 *
 * Работаем со строкой ДО разбора JSON: это одна замена по всему телу вместо
 * обхода дерева объектов, и она автоматически покрывает любые новые поля —
 * сегодня это `audio_url`, завтра будет `preview_url`, и никто не вспомнит.
 */
function absolutiseLinks(raw: string): string {
  if (!API_ORIGIN) return raw;
  // На вебе адрес совпадает с текущим — замена ничего не меняет, но и не мешает.
  return raw.split('"/api/v1/').join(`"${API_ORIGIN}/api/v1/`);
}

/**
 * The backend wraps many (but not all) responses in an envelope:
 *   { status: number, message: string, data: <payload> }
 * Callers expect the bare payload (e.g. login returns { access_token, user }).
 * Unwrap when the envelope is present; leave already-bare responses
 * (e.g. /auth/me) untouched.
 *
 * Additionally, list endpoints are inconsistent about the array field name
 * (`marathons`, `words`, `submissions`, …) while the frontend uniformly reads
 * `.items`. So when the payload is an object with an array field but no `items`,
 * alias that array to `items`.
 */
function unwrap<T = unknown>(body: T): T {
  const rec = body as Record<string, unknown> | null;
  if (
    rec &&
    typeof rec === 'object' &&
    'data' in rec &&
    'message' in rec &&
    typeof rec.status === 'number'
  ) {
    return normalizeList(rec.data) as T;
  }
  return body;
}

// Alias the first array-valued field to `items` for list pages that read `.items`.
function normalizeList<T>(payload: T): T {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const obj = payload as Record<string, unknown>;
    if (!('items' in obj)) {
      for (const k of Object.keys(obj)) {
        if (Array.isArray(obj[k])) {
          obj.items = obj[k];
          break;
        }
      }
    }
  }
  return payload;
}

/**
 * Axios client with:
 *  - automatic Bearer auth header injection
 *  - one-shot refresh-on-401 retry (rotating refresh token, see auth-service Refresh())
 *  - centralised error shape (`{ error: string }`) handling
 *
 * Concurrent 401s are coalesced via a single in-flight refresh promise so we
 * don't fire N parallel /auth/refresh calls when many requests fail at once.
 */
export const api = axios.create({
  baseURL: BASE_PATH,
  withCredentials: false,
  timeout: 30_000,
  // На сайте и при выключенном CapacitorHttp — штатный XHR без изменений; в приложении с pinning обёртка
  // возвращает запросам метку сборки, таймаут и двоичный ответ (`lib/nativeHttp.ts`).
  adapter: nativeSafeAdapter,
  // Сначала разворачиваем ссылки в сыром теле, затем отдаём его обычному
  // разбору JSON: порядок важен, наша функция работает со строкой.
  transformResponse: [
    (data: unknown) => (typeof data === 'string' ? absolutiseLinks(data) : data),
    ...([] as unknown[]).concat(axios.defaults.transformResponse ?? []),
  ] as never,
});

// --- Request interceptor: attach access token ---
/**
 * Что добавляется к каждому запросу: ключ доступа и номер устройства.
 *
 * ⚠️ ПЕРЕХВАТЧИК АСИНХРОННЫЙ НАМЕРЕННО. Номер устройства лежит в защищённом
 * хранилище платформы, а оно читается не мгновенно. Взять его синхронно нельзя,
 * а отправлять первые запросы без номера значит потерять именно тот запрос,
 * ради которого всё и делается, — вход: сессия записывается с устройством
 * ровно в этот момент. Чтение происходит один раз, дальше номер в памяти.
 */
export async function attachRequestIdentity<T extends { headers?: Record<string, unknown> }>(
  config: T,
): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.headers) {
    try {
      config.headers['X-Device-Id'] = await getDeviceId();
    } catch {
      // Номер не получен — запрос всё равно уходит. Сессия запишется без
      // устройства, в сводке она будет «неизвестным»; отказать во входе
      // из-за служебного номера было бы несоразмерно.
    }
  }
  return config;
}

api.interceptors.request.use((config) => attachRequestIdentity(config as never) as never);

// --- Response interceptor: refresh on 401 ---
/**
 * Итог продления. `noReply` — сервер не ответил вовсе (обрыв, таймаут): запрос, ради которого продлевали,
 * падает тогда как «нет сети», а не как 401 — вход в порядке, не в порядке связь.
 */
type RefreshResult = { token: string | null; noReply?: AxiosError };
let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * ⚠️ У ПРОДЛЕНИЯ ЕСТЬ ТАЙМАУТ (02.10.2026). До этого `axios.post` шёл без него: на сайте запрос мог висеть,
 * пока не сдастся браузер, а в приложении с CapacitorHttp — 600 с, и всё это время за ним стояла вся очередь
 * 401 (`refreshInFlight` один на всех). Таймаут — такой же сбой связи, как обрыв: сессию не стирает.
 */
export const REFRESH_TIMEOUT_MS = 30_000;

/** Транспорт продления. Отдельным объектом — проверки подменяют `post`, как `loginReload.go`. */
export const refreshTransport = {
  post: (url: string, body: { refresh_token?: string | null }) =>
    axios.post(url, body, { adapter: nativeSafeAdapter, timeout: REFRESH_TIMEOUT_MS }),
};

/**
 * Что лежит в хранилище прямо сейчас — возможно, свежее того, что в памяти.
 *
 * Читаем ровно то, что записывает хранилище состояния (ключ `onebilim-auth`).
 * Любая неожиданность — сломанный JSON, недоступное хранилище, чужой формат —
 * означает «ничего не знаем»: тогда обновление пойдёт обычным путём.
 */
/**
 * Выход, после которого страница загружается заново.
 *
 * ⚠️ БЕЗ ПЕРЕЗАГРУЗКИ ЧЕЛОВЕК ОСТАЁТСЯ НА СТАРОМ КОДЕ — И СНОВА ВЫЛЕТАЕТ.
 *
 * Платформа живёт одной страницей: после выхода человек видит экран входа, но
 * САМА СТРАНИЦА не перезагружается, и в браузере продолжает работать та сборка,
 * с которой он начал день. То есть выкаченное исправление до него не доходит —
 * он входит заново тем же сломанным кодом и вылетает опять.
 *
 * Проверено 07.09.2026: правка против вылетов выложена в 12:50, а в 13:12 и
 * 13:15 люди вылетали снова — и по журналу шлюза продолжали грузить сборки
 * index-BLKH7WJo и index-Hf_LBuxo, то есть вчерашние. Заголовки кеша при этом
 * настроены верно: страница отдаётся с no-cache, достаточно её перезагрузить.
 *
 * Поэтому вынужденный выход теперь перезагружает страницу: человек попадает на
 * экран входа уже со свежим кодом.
 */
function logoutAndReload(auth: { logout: () => void }): void {
  auth.logout();
  // ⚠️ ТОЛЬКО В БРАУЗЕРЕ. В приложении из магазина код меняется установкой
  // обновления, а не перезагрузкой страницы, — там перезагружать нечего, зато
  // можно нарваться на путаницу с адресом: приложение открыто не по обычному
  // http-адресу, и переход по пути внутри него ведёт себя иначе.
  if (typeof window !== 'undefined' && !isStoreBuild()) {
    const { pathname, search } = window.location;
    // 🔴 УЖЕ НА ЭКРАНЕ ВХОДА — НЕ ПЕРЕЗАГРУЖАЕМ (26.09.2026, техдолг 299). Перезагрузка отсюда ничего не
    // даёт, а любой запрос, который при старте получит 401, превращает её в петлю: на проде вкладка на
    // экране входа перезагружалась раз в секунду сутками, и ученик не успевал ввести пароль.
    if (isAuthScreen(pathname)) return;
    // Заменяем запись в истории, чтобы «назад» не возвращал в закрытую сессию. Путь урока едет в
    // `?next=`: после входа ученик вернётся туда же, а его ответы уйдут из очереди сами.
    loginReload.go(loginPathWithNext(pathname + search));
  }
}

/**
 * Переход на вход с перезагрузкой. Отдельным объектом — проверки подменяют `go`: в jsdom location не подменить.
 * Перезагрузка намеренная — флаг запуска чистый, иначе следующий старт принял бы её за падение (T23).
 */
export const loginReload = {
  go: (url: string) => {
    markCleanExit();
    window.location.replace(url);
  },
};

async function readStoredTokens(): Promise<{
  accessToken?: string;
  refreshToken?: string;
} | null> {
  try {
    const raw = await sessionStorageAdapter.getItem('onebilim-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string; refreshToken?: string } };
    return parsed?.state ?? null;
  } catch {
    return null;
  }
}

async function refresh(): Promise<RefreshResult> {
  const auth = useAuthStore.getState();
  if (!auth.refreshToken) {
    // ⚠️ СНАЧАЛА УБЕДИМСЯ, ЧТО СЕССИЮ ВООБЩЕ УСПЕЛИ ПРОЧИТАТЬ.
    //
    // В приложении сессия лежит в защищённом хранилище платформы, а оно
    // АСИНХРОННОЕ: в первые мгновения после запуска токенов «ещё нет», хотя они
    // есть. Если в этот момент какой-нибудь запрос получит 401, мы окажемся
    // здесь — и, не проверив готовность, сотрём живую сессию своими руками.
    //
    // Именно это и происходило после каждого обновления из TestFlight: новая
    // сборка — холодный запуск — гонка чтения хранилища с первым запросом —
    // выход из аккаунта. Заказчик 23.08.2026: «не нравится, что после каждого
    // обновления нужно логиниться заново».
    //
    // Пока хранилище не прочитано, ничего не стираем: запрос просто не удастся,
    // а следующий уже пройдёт с восстановленным токеном.
    if (!useAuthStore.persist.hasHydrated()) return { token: null };

    // Хранилище прочитано, и токена обновления в нём действительно нет — сессия
    // испорчена и починиться сама не может. Честнее вывести на вход, чем
    // оставить человека перед вечно пустым экраном (21.08.2026).
    logoutAndReload(auth);
    return { token: null };
  }

  // ⚠️ СНАЧАЛА СМОТРИМ, НЕ ОБНОВИЛА ЛИ ВХОД СОСЕДНЯЯ ВКЛАДКА.
  //
  // Вкладки делят одно хранилище, но каждая держит СВОЮ копию токенов в памяти.
  // Открыл человек платформу в двух вкладках — и через полчаса вторая идёт
  // обновляться тем токеном, который первая уже потратила. Сервер видит
  // повторное использование потраченного токена, считает это кражей и гасит всю
  // цепочку — вылетают ОБЕ вкладки. Прощает он такой повтор только в пределах
  // минуты, а между вкладками проходят десятки минут.
  //
  // Поэтому перед запросом перечитываем хранилище: если соседка уже обновила
  // вход, просто берём её свежий ключ и к серверу не идём вовсе. Ни лишнего
  // запроса, ни ложной тревоги о краже.
  const stored = await readStoredTokens();
  if (stored?.accessToken && stored.accessToken !== auth.accessToken) {
    auth.setTokens(stored.accessToken, stored.refreshToken ?? auth.refreshToken);
    return { token: stored.accessToken };
  }
  const refreshToken = stored?.refreshToken ?? auth.refreshToken;

  try {
    const resp = await refreshTransport.post(`${BASE_PATH}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    const { access_token, refresh_token } = unwrap(resp.data);
    if (!access_token) return { token: null };
    auth.setTokens(access_token, refresh_token ?? auth.refreshToken);
    return { token: access_token };
  } catch (e) {
    // ⚠️ ВЫХОДИМ ТОЛЬКО ЕСЛИ СЕРВЕР СКАЗАЛ «ЭТОТ ВХОД НЕДЕЙСТВИТЕЛЕН».
    //
    // Здесь стоял `catch { logout() }` — выход по ЛЮБОЙ неудаче. А неудача
    // бывает разной: сервер может ответить «токен не годится» (тогда выход
    // честен), а может не ответить вовсе — метро, лифт, заснувший телефон,
    // моргнувшая сеть, перезапуск службы. Во втором случае вход в полном
    // порядке, но человека выбрасывало на экран пароля.
    //
    // Заказчик 07.09.2026: «почему вылетает у всех каждые 30-40 минут». По
    // журналам сервер в это время работал ровно: 493 успешных обновления из
    // 503 за три часа, отказов всего 10. То есть сервер почти никого не
    // выгонял — выгоняла эта строка, и следов не оставалось нигде: запрос,
    // который не дошёл, в журнал сервера не попадает.
    //
    // Восемь учеников из десяти сидят с телефона, где обрыв связи — обычное
    // дело, а обновление случается каждые пятнадцать минут. Отсюда и «у всех».
    //
    // Теперь при сетевом сбое сессия остаётся: следующий запрос попробует
    // снова и, скорее всего, пройдёт.
    //
    // ⚠️ ТАЙМАУТ ПРОДЛЕНИЯ — ТОТ ЖЕ СЕТЕВОЙ СБОЙ (02.10.2026). У него нет ответа сервера, значит и статуса нет:
    // сессия остаётся. Наружу уходит `noReply` — перехватчик покажет «нет соединения» и отдаст запросу
    // сетевую ошибку вместо 401; следующий запрос попробует продлить заново.
    const err = e as AxiosError | undefined;
    const status = err?.response?.status;
    if (status === 401 || status === 403) logoutAndReload(auth);
    if (!err?.response && axios.isAxiosError(err)) return { token: null, noReply: err };
    return { token: null };
  }
}

api.interceptors.response.use(
  (r) => {
    r.data = unwrap(r.data);
    return r;
  },
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Ответа сервера нет вовсе (обрыв, таймаут) — общий знак «нет соединения», одно место на всё
    // приложение (`lib/networkNotice.ts`). Отменённый запрос — не обрыв: его отменили мы сами.
    if (!error.response && !axios.isCancel(error)) reportNetworkError();

    if (status !== 401 || !original || original._retry) {
      return Promise.reject(normaliseError(error));
    }
    original._retry = true;

    // Coalesce concurrent refresh attempts
    refreshInFlight ??= refresh().finally(() => {
      refreshInFlight = null;
    });
    const { token: newToken, noReply } = await refreshInFlight;
    if (!newToken) {
      // Продление не дошло до сервера — это обрыв, а не отказ во входе: тот же знак и та же ошибка, что у
      // любого запроса без ответа. Очередь ответов считает её временной и повторит.
      if (noReply) {
        reportNetworkError();
        return Promise.reject(normaliseError(noReply));
      }
      return Promise.reject(normaliseError(error));
    }

    original.headers = original.headers ?? {};
    (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    return api(original);
  },
);

/** Когда истекает ключ доступа, мс эпохи. Не разобрался — `null`: тогда остаётся обычный путь через 401. */
function tokenExpiresAt(token: string | null | undefined): number | null {
  const part = token?.split('.')[1];
  if (!part) return null;
  try {
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Продлить ключ ЗАРАНЕЕ, если он не проживёт `minLifeMs` (30.10.2026, T5, `docs/tasks/T5-ios-progress-banner.md`).
 *
 * 🔴 ЗАЧЕМ. Системный nginx принимает тело запроса ЦЕЛИКОМ и только потом отдаёт его шлюзу, а шлюз проверяет
 * ключ. Загрузка записи на медленной связи идёт минуты: начатая с ключом на последних минутах, она получает 401
 * уже после того, как ушёл весь файл, — и повтор после продления шлёт его заново. На проде 28.10 так ушла
 * впустую 4-мегабайтная запись ученицы iOS 1.1.1. Продление — тем же замком, что у перехватчика 401: параллельные
 * загрузки и запросы не продлевают дважды. Не вышло (нет связи, сервер отказал) — молча идём дальше: дальше
 * работает обычный путь «401 → продлить → повторить».
 */
export async function ensureTokenLife(minLifeMs: number): Promise<void> {
  const expiresAt = tokenExpiresAt(useAuthStore.getState().accessToken);
  if (expiresAt === null || expiresAt - Date.now() >= minLifeMs) return;
  refreshInFlight ??= refresh().finally(() => {
    refreshInFlight = null;
  });
  await refreshInFlight.catch(() => undefined);
}

/**
 * `code` — код axios (`ERR_NETWORK`, `ECONNABORTED`…), только чтобы отличить обрыв и таймаут от ответа
 * сервера (`lib/errors.ts` `isNetworkError`). Код отказа СЕРВЕРА лежит не здесь, а в `raw.code`
 * (`lib/apiErrorCode.ts`). `message` без ответа — библиотечный текст: на экран его не выводить, только
 * через `humanizeError`.
 */
export type ApiError = {
  status: number;
  message: string;
  code?: string;
  raw?: unknown;
};

function normaliseError(err: AxiosError): ApiError {
  const data = err.response?.data as { error?: string; message?: string } | undefined;
  return {
    status: err.response?.status ?? 0,
    message: data?.error || data?.message || err.message || 'Request failed',
    code: err.code,
    raw: err.response?.data,
  };
}
